// Contrato canônico do evento SYNQi3 — a FONTE ÚNICA, espelhada no sdk-py (synqi3_sdk/contract.py).
// A suíte de conformidade garante que as duas implementações concordam. Agnóstico (REGRA #1):
// nomes de evento genéricos, sem hardcode de vertical.
//
//  - event_id gerado no cliente = idempotência (D.5.5). O motor deduplica por ele.
//  - type em underscore (REGRA #8); o SDK normaliza dot->underscore na origem.
//  - classificação sinal/analítico (D.5.1) governa a política de envio (imediato vs lote).

// Eventos de SINAL por padrão: os decisórios (mudam a decisão de personalização). Genéricos,
// configuráveis pelo conector. Todo o resto é analítico.
export const DEFAULT_SIGNAL_EVENTS = Object.freeze([
  "identify",
  "conversion",
  "order_paid",
  "purchase",
  "checkout_started",
  "checkout_completed",
  "signup",
  "subscription_start",
  "cart_abandoned",
  "flow_abandoned",
  "form_submitted",
]);

// Gatilhos de flush do lote analítico (D.5.2). Valores de PARTIDA, calibrados na Fase 0.
export const DEFAULT_FLUSH = Object.freeze({
  bufferSize: 10, // regime normal de navegação
  windowMs: 5000, // piso de frescor
  maxBatch: 50, // teto de segurança
});

// TETO DO CORPO, em bytes — e ele não é nosso: é o portão. ESPELHO de sdk-py contract.MAX_BODY_BYTES.
//
// Medido em 26/09/2026 contra `https://api.synqi3.ai/events`, com token INVÁLIDO de propósito (o
// authorizer recusa, então nada entra) variando só o tamanho:
//
//      1 evento       222 B   403 {"message":"Forbidden"}   <- authorizer: credencial
//     25 eventos    5.262 B   403 {"message":"Forbidden"}   <- authorizer: credencial
//     50 eventos   10.512 B   403 <!DOCTYPE HTML …          <- CloudFront/WAF, ANTES do authorizer
//
// Os DOIS respondem 403 e só o corpo os distingue. Quem leva o 403 grande não recebe resposta nossa
// nenhuma — o pedido morreu antes de chegar —, então o servidor não tem como avisar: o único
// remédio é o lote caber. `maxBatch: 50` sozinho NÃO protege: 50 eventos de ~200 B são ~10 KB.
export const MAX_BODY_BYTES = 8192;

// Margem para o envelope e para o `tenant_id` que o transporte acrescenta ao corpo (`transport.js`
// serializa `{ tenant_id, events }`, enquanto a medição olha só `{ events }`).
export const BODY_SAFETY_BYTES = 512;

// Janela e limites da persistência/replay (D.5.4).
export const REPLAY_MAX_AGE_MS = 24 * 60 * 60 * 1000; // evento pendente > 24h é descartado

/** Gera um UUID v4 para usar como event_id (idempotência, D.5.5). */
export function newEventId() {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    return (c === "x" ? r : (r & 0x3) | 0x8).toString(16);
  });
}

/** Normaliza o nome do evento: minúsculo, sem espaços nas pontas, dot->underscore (REGRA #8). */
export function normalizeType(type) {
  return String(type == null ? "" : type).trim().toLowerCase().replace(/\./g, "_");
}

function toArray(signalSet) {
  return (Array.isArray(signalSet) ? signalSet : Array.from(signalSet)).map(normalizeType);
}

/** True se o tipo é um evento de SINAL (envio imediato). D.5.1. */
export function isSignal(type, signalSet = DEFAULT_SIGNAL_EVENTS) {
  return toArray(signalSet).includes(normalizeType(type));
}

/** "signal" | "analytic" — a classe que governa a política de envio. */
export function classify(type, signalSet = DEFAULT_SIGNAL_EVENTS) {
  return isSignal(type, signalSet) ? "signal" : "analytic";
}

/**
 * Constrói um evento normalizado, pronto para o wire de POST /events.
 * Campos: event_id (cliente), type (underscore), client_ts (ms), e os de contexto presentes.
 */
/** Os campos de contexto do navegador, no lugar onde o ingest os guarda. */
function CONTEXTO(input) {
  const fora = {};
  for (const k of ["url", "referrer", "user_agent"]) {
    if (input && input[k] != null) fora[k] = input[k];
  }
  return fora;
}

export function buildEvent(input) {
  const type = normalizeType(input && input.type);
  if (!type) throw new Error("event type is required");
  const ev = {
    event_id: (input && input.event_id) || newEventId(),
    type,
    client_ts: input && input.client_ts != null ? input.client_ts : Date.now(),
  };
  for (const k of ["user_id", "anonymous_id", "session_id"]) {
    if (input && input[k] != null) ev[k] = input[k];
  }
  // ONDE `url`, `referrer` E `user_agent` MORAM.
  //
  // No TOPO do evento, eles são jogados fora. O `apps/ingest` monta o registro campo a campo
  // (`event_id, tenant_id, type, user_id, anonymous_id, entity_id, session_id, ts, client_ts,
  // props`) e a tabela `synqi3_core_lake.events` tem exatamente essas colunas, conferido no Glue
  // em 29/09/2026. Nada mais atravessa.
  //
  // Os dois SDKs mandavam os três no topo desde o commit inicial. Eram ~200 bytes por evento
  // gastos do orçamento de 8.192 do portão, entregues com HTTP 200, e sumindo em silêncio: 200 é o
  // ingest aceitando o LOTE, não o campo sendo guardado.
  //
  // `props` é mapa livre e É persistido. Então eles vão para lá. O que o chamador escreveu em
  // `props` VENCE: quem foi explícito não tem o valor trocado por um default de contexto.
  ev.props = { ...CONTEXTO(input), ...((input && input.props) || {}) };
  return ev;
}
